/**
 * Permissions Hook - Manages user permissions and role-based access control.
 * Role is sourced from the authenticated session (backend), not derived client-side.
 */

import { useMemo } from 'react'
import { useSession } from './use-session'

export type UserRole = 'admin' | 'user'

export interface UserPermissions {
  // Credit management permissions
  canViewAllUserCredits: boolean
  canPurchaseCredits: boolean
  canSetUserCaps: boolean
  canResetUserUsage: boolean
  canViewWorkspaceAnalytics: boolean

  // General workspace permissions
  canManageMembers: boolean
  canManageWorkspaceSettings: boolean
  canViewBilling: boolean
}

const ADMIN_PERMISSIONS: UserPermissions = {
  canViewAllUserCredits: true,
  canPurchaseCredits: true,
  canSetUserCaps: true,
  canResetUserUsage: true,
  canViewWorkspaceAnalytics: true,
  canManageMembers: true,
  canManageWorkspaceSettings: true,
  canViewBilling: true,
}

const USER_PERMISSIONS: UserPermissions = {
  canViewAllUserCredits: false,
  canPurchaseCredits: false,
  canSetUserCaps: false,
  canResetUserUsage: false,
  canViewWorkspaceAnalytics: false,
  canManageMembers: false,
  canManageWorkspaceSettings: false,
  canViewBilling: false,
}

/**
 * Hook for managing user permissions based on role from the backend session.
 */
export function usePermissions() {
  const { user } = useSession()

  const userRole: UserRole = useMemo(() => {
    return user?.role === 'admin' ? 'admin' : 'user'
  }, [user])

  const permissions = useMemo(
    () => (userRole === 'admin' ? ADMIN_PERMISSIONS : USER_PERMISSIONS),
    [userRole],
  )

  return {
    userRole,
    permissions,
    isAdmin: userRole === 'admin',
    isUser: userRole === 'user',
  }
}

/**
 * Hook for checking a specific permission.
 */

/**
 * Convenience wrapper for credit-admin permission checks.
 */
